// Shared types for the categories feature.
// Mirrors apps/api/internal/product.CategoryNode (camelCase JSON).

export interface Category {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  productCount: number;
  children: Category[];
}

export interface CategoryFormPayload {
  name: string;
  slug: string;
  parentId: string | null;
}

/** Flatten a category tree to a depth-prefixed list, optionally excluding a subtree. */
export function flattenCategories(
  cats: Category[],
  excludeId?: string,
): Array<{ id: string; label: string }> {
  const out: Array<{ id: string; label: string }> = [];
  function walk(list: Category[], depth: number) {
    for (const c of list) {
      if (c.id === excludeId) continue;
      out.push({ id: c.id, label: "  ".repeat(depth) + c.name });
      if (c.children?.length) walk(c.children, depth + 1);
    }
  }
  walk(cats, 0);
  return out;
}

/** Flatten a category tree preserving each node's depth, for indented rendering. */
export function flattenWithDepth(
  cats: Category[],
): Array<{ cat: Category; depth: number }> {
  const out: Array<{ cat: Category; depth: number }> = [];
  function walk(list: Category[], depth: number) {
    for (const c of list) {
      out.push({ cat: c, depth });
      if (c.children?.length) walk(c.children, depth + 1);
    }
  }
  walk(cats, 0);
  return out;
}

/** Total category count (including children). */
export function countCategories(cats: Category[]): number {
  let n = 0;
  function walk(list: Category[]) {
    for (const c of list) {
      n += 1;
      if (c.children?.length) walk(c.children);
    }
  }
  walk(cats);
  return n;
}

/** Sum of productCount across the entire tree. */
export function sumProductCount(cats: Category[]): number {
  let n = 0;
  function walk(list: Category[]) {
    for (const c of list) {
      n += c.productCount ?? 0;
      if (c.children?.length) walk(c.children);
    }
  }
  walk(cats);
  return n;
}

/** Find a category by id within the tree, or null. */
export function findCategory(cats: Category[], id: string): Category | null {
  for (const c of cats) {
    if (c.id === id) return c;
    if (c.children?.length) {
      const hit = findCategory(c.children, id);
      if (hit) return hit;
    }
  }
  return null;
}

/** Filter the tree, keeping any branch that contains a node whose name matches `q`. */
export function filterTree(cats: Category[], q: string): Category[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return cats;
  function walk(list: Category[]): Category[] {
    const out: Category[] = [];
    for (const c of list) {
      const childMatches = walk(c.children ?? []);
      const selfMatches = c.name.toLowerCase().includes(needle);
      if (selfMatches || childMatches.length > 0) {
        out.push({ ...c, children: childMatches });
      }
    }
    return out;
  }
  return walk(cats);
}
